/**
 * Croatian overrides for the fiveS screens.
 *
 * Only what genuinely differs from Bosnian. Croatian is ijekavica too, so
 * nothing moves for the jat reflex — what changes is vocabulary: *sustav*,
 * *tvrtka*, *datoteka*, *sigurnost*, *tipka*, and borrowed verbs in -irati.
 *
 * An entry identical to its Bosnian base is a bug, not a no-op: i18n.test.ts
 * fails on one, because a line that changes nothing is a decision that was
 * reverted in bs.ts without anybody revisiting this file.
 *
 * The word that moves most here is *kriterijum* — Croatian has *kriterij*,
 * and it is on almost every line of a screen made of criteria.
 */

import type { FiveSSlice } from '../bs/fiveS'

export const fiveS: Partial<FiveSSlice> = {
  // *stroj* for a machine, which is what carries the help text across.
  'fiveS.help.oneWalk':
    'Jedan obilazak po stroju, po danu, po smjeni. Jutarnji i noćni obilazak istog stroja istog dana su dva odvojena zapisa, jer opisuju dva različita stanja centra.',

  'fiveS.date.help':
    'Upisujete dan kad ste stvarno obišli stroj, a ne dan kad unosite ocjene — zapis opisuje stanje centra tog dana.',
  // *tko*, *razdoblje*, *obvezno*.
  'fiveS.workers.hint': 'Tko je održavao centar u ovom razdoblju. Nije obvezno.',
  // *popis* rather than *spisak*, and the machine again.
  'fiveS.workers.help':
    'Iz ovog popisa se računa 5S ocjena po radniku, a ona opisuje stanje stroja koji su držali, ne njih same.',
  // *smjena* is shared; only *obvezno* moves.
  'fiveS.shift.hint': 'Koja smjena je držala centar. Nije obvezno.',

  // *lista* → *popis*.
  'fiveS.onlyUnanswered.hint':
    'Korisno pri kraju obilaska, da ne tražite propušteno po cijelom popisu.',

  'fiveS.progressLabel': 'Ocijenjeni kriteriji',
  // *postotak*, *zbroj*, *uspoređivati*.
  'fiveS.percentage.help':
    'Postotak se koristi jer radni centri mogu imati različite skale, pa se sirovi zbrojevi ne mogu uspoređivati.',

  // *spremiti* rather than *sačuvati*, everywhere a draft is involved.
  'fiveS.saveDraft': 'Spremi nacrt',
  'fiveS.submit.hint':
    'Predaja je moguća tek kada su svi kriteriji ocijenjeni. Nacrt možete spremiti bilo kada.',
  // *predana*, and the machine.
  'fiveS.submit.help':
    'Predana evaluacija se više ne mijenja jer je to zapis stanja stroja tog dana; ponovo je otvara samo administrator.',
  'fiveS.submit.blocked': 'Prije predaje ocijenite sve kriterije. Neocijenjeno: {count}.',
  'fiveS.draftSaved': 'Nacrt je spremljen.',
  // *predan*, not *predata*.
  'fiveS.submitted': 'Evaluacija je predana — {score}.',
  'fiveS.submitted.next': 'Evaluacija je predana — {score}. Odaberite sljedeći radni centar.',
  'fiveS.saveFailed': 'Spremanje nije uspjelo.',

  'fiveS.centerChange.keepHint':
    'Ako ih želite spremiti, prvo zatvorite ovaj prozor i pritisnite „Spremi nacrt”.',

  // *stroj*, and printing is *ispisivanje* rather than *štampanje*.
  'fiveS.stickers.subtitle':
    'Naljepnice za strojeve. Radnik skenira kod i prijava se otvara s već odabranim radnim centrom.',
  'fiveS.stickers.printHint':
    'Ispišite na A4, izrežite po isprekidanoj liniji i plastificirajte. Zalijepite u visini očiju, dalje od prašine i ulja.',
  'fiveS.stickers.printAll': 'Ispiši sve',
  'fiveS.stickers.printSelected': 'Ispiši ({count})',
  'fiveS.stickers.clearSelection': 'Poništi odabir',
  'fiveS.stickers.select': 'Odaberi {name}',
  'fiveS.stickers.hasCode': 'Izdan',
  'fiveS.stickers.regenerateConfirm':
    'Obnoviti QR kod za „{name}"?\n\nNaljepnica koja je trenutno na stroju odmah prestaje vrijediti. Morat ćete ispisati i zalijepiti novu.',

  // *račun* rather than *nalog*, and an ID card is an *osobna iskaznica*.
  'fiveS.badge.warning':
    'Kartica je kao ključ — tko je ima, može se prijaviti ovim računom, pa i fotografija kartice. Čuvajte je kao osobnu iskaznicu. Ako se izgubi, izdajte novu; stara odmah prestaje vrijediti.',
  'fiveS.list.readOnly': '5S evaluacije obavljaju odgovorne osobe — ovdje ih možete pregledati.',

  'fiveS.insights.rank.title.all': 'Mjesto u tvrtki ovog mjeseca',
  'fiveS.insights.rank.plantAverage.all': 'Prosjek tvrtke',

  // ---- The 5S board and the walk -----------------------------------
  //
  // *Stroj* for every *mašina*, and with it the gender moves: *obiđen*,
  // *nijedan*, *vaš*. Counted machines take *stroja* / *strojeva*.
  'fiveS.page.subtitle': 'Koje strojeve još treba obići ovaj mjesec i kako su prošli obiđeni.',
  'fiveS.board.coverage.one': '{walked} od {count} stroja obiđeno',
  'fiveS.board.coverage.few': '{walked} od {count} stroja obiđeno',
  'fiveS.board.coverage.many': '{walked} od {count} strojeva obiđeno',
  'fiveS.board.coverage.none': 'Nema strojeva za obilazak',
  'fiveS.board.coverage.label': 'Obiđeni strojevi',
  'fiveS.board.allDone': 'Svi strojevi su obiđeni.',
  'fiveS.board.withoutTemplate.help':
    'Stroj bez 5S obrasca ne može se ocijeniti, pa se ne broji. Obrazac dodaje administrator.',
  'fiveS.board.filter.aria': 'Koje strojeve prikazati',
  'fiveS.board.filter.walked': 'Obiđeni',
  'fiveS.board.filter.mine': 'Moji strojevi',
  'fiveS.board.col.machine': 'Stroj',
  'fiveS.board.mine': 'vaš',
  'fiveS.board.status.walked': 'Obiđen',
  'fiveS.board.evaluateMachine': 'Ocijeni stroj {name}',
  'fiveS.board.empty.title': 'Nema strojeva u vašim pogonima',
  'fiveS.board.empty.description':
    'Strojeve dodaje administrator u Administraciji, pod Radni centri. Ovdje se pojave čim postoje.',
  'fiveS.board.empty.allWalked': 'Svi strojevi su obiđeni za {month}.',
  'fiveS.board.empty.noneWalked': 'Za {month} još nijedan stroj nije obiđen.',
  'fiveS.board.empty.mine': 'Nijedan stroj nije dodijeljen vama.',
  'fiveS.board.empty.showAll': 'Prikaži sve strojeve',
  // *odabrati*, *kriterij*.
  'fiveS.walk.subtitle': 'Odaberite stroj, ocijenite svaki kriterij i predajte.',
  'fiveS.picker.title': 'Koji stroj ocjenjujete?',
  'fiveS.picker.none': 'Nijedan stroj ne odgovara pretrazi.',
  'fiveS.picker.walked': 'Već obiđeni ovaj mjesec',
  'fiveS.picker.change': 'Drugi stroj',
  'fiveS.scan.button': 'Skeniraj stroj',
  'fiveS.scan.title': 'Skenirajte QR naljepnicu stroja',
  'fiveS.scan.hint': 'Usmjerite kameru na naljepnicu; stroj se otvara sam.',
  'fiveS.scan.opening': 'Tražim stroj…',
  'fiveS.scan.again': 'Pokušaj ponovno',
  'fiveS.detail.all': 'Svi kriteriji',
  'fiveS.jump.aria': 'Još {count}: idi na prvi neocijenjeni kriterij',
  'fiveS.insights.criteria.hint': 'Kriteriji ocijenjeni ispod {target} skale, najslabiji prvi.',
  'fiveS.insights.criteria.none': 'Svi kriteriji su na cilju ili iznad njega.',
  // *spremiti* for a saved walk, as for the draft above.
  'fiveS.list.empty.hint': 'Svaki obilazak se pojavi ovdje čim se spremi, najnoviji prvi.',
}
